'use client'

import { Pause, Play } from 'lucide-react'
import React, { useEffect, useState } from 'react'

import { heroVideoPlaybackButtonClassName } from '@/components/Hero/HeroVideoBackground'
import { cn } from '@/utilities/ui'

export const PARTNER_LOGOS = [
  { src: '/media/homepage/partners/group-7.svg', nameKey: 'urbanSports' as const },
  { src: '/media/homepage/partners/heineken.svg', nameKey: 'heineken' as const },
  { src: '/media/homepage/partners/partner-1.svg', nameKey: 'decathlon' as const },
  { src: '/media/homepage/partners/partner-2.svg', nameKey: 'milesAndMore' as const },
  { src: '/media/homepage/partners/tulu.svg', nameKey: 'tulu' as const },
  { src: '/media/homepage/partners/partner-3.svg', nameKey: 'studierendenwerk' as const },
  { src: '/media/homepage/partners/partner-7.svg', nameKey: 'flink' as const },
] as const

export type PartnerLogo = (typeof PARTNER_LOGOS)[number]

type PartnerLogosMarqueeProps = {
  ariaLabel: string
  heading?: string
  headingId?: string
  partners: Record<PartnerLogo['nameKey'], string>
  pauseLabel?: string
  playLabel?: string
}

const MARQUEE_SEGMENT_REPEATS = 4

function buildMarqueeSegment() {
  return Array.from({ length: MARQUEE_SEGMENT_REPEATS }, (_, repeatIndex) =>
    PARTNER_LOGOS.map((logo) => ({
      logo,
      key: `${repeatIndex}-${logo.nameKey}`,
    })),
  ).flat()
}

export function PartnerLogosMarquee({
  ariaLabel,
  heading,
  headingId,
  partners,
  pauseLabel = 'Pause carousel',
  playLabel = 'Play carousel',
}: PartnerLogosMarqueeProps) {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const trackRef = React.useRef<HTMLUListElement>(null)
  const [marqueeDistance, setMarqueeDistance] = useState<number | null>(null)

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    const updatePreference = () => setPrefersReducedMotion(media.matches)

    updatePreference()
    media.addEventListener('change', updatePreference)

    return () => media.removeEventListener('change', updatePreference)
  }, [])

  const segment = buildMarqueeSegment()
  const track = prefersReducedMotion
    ? PARTNER_LOGOS.map((logo) => ({ logo, key: logo.nameKey }))
    : [...segment, ...segment]

  useEffect(() => {
    if (prefersReducedMotion) return

    const trackElement = trackRef.current
    if (!trackElement) return

    const updateDistance = () => {
      setMarqueeDistance(trackElement.scrollWidth / 2)
    }

    updateDistance()

    const observer = new ResizeObserver(updateDistance)
    observer.observe(trackElement)

    return () => observer.disconnect()
  }, [prefersReducedMotion])

  return (
    <div className="w-full">
      <div className="px-4">
        {heading ? (
          <h2
            className="w-full text-center font-display text-[1.5rem] font-semibold leading-tight tracking-[-0.04em] text-black md:text-[2rem]"
            id={headingId}
          >
            {heading}
          </h2>
        ) : null}
      </div>

      <div className="mt-9 w-full overflow-hidden motion-reduce:-mx-4 motion-reduce:overflow-x-auto motion-reduce:px-4">
        <ul
          ref={trackRef}
          aria-label={ariaLabel}
          data-paused={isPaused}
          className={cn(
            'flex w-max items-center gap-8 sm:gap-12',
            !prefersReducedMotion && marqueeDistance && 'partner-logos-marquee',
          )}
          style={
            marqueeDistance && !prefersReducedMotion
              ? ({
                  '--marquee-distance': `${marqueeDistance}px`,
                  animationPlayState: isPaused ? 'paused' : 'running',
                } as React.CSSProperties)
              : undefined
          }
        >
          {track.map((item, index) => {
            const isDuplicate = !prefersReducedMotion && index >= segment.length

            return (
              <li key={`${item.key}-${index}`} aria-hidden={isDuplicate || undefined}>
                <img
                  alt={partners[item.logo.nameKey]}
                  className="h-8 w-auto max-w-none shrink-0 object-contain sm:h-10 lg:h-12"
                  height={48}
                  loading="lazy"
                  src={item.logo.src}
                  width={180}
                />
              </li>
            )
          })}
        </ul>
      </div>

      {!prefersReducedMotion && (
        <div className="mt-9 flex justify-center px-4">
          <button
            type="button"
            aria-label={isPaused ? playLabel : pauseLabel}
            aria-pressed={isPaused}
            onClick={() => setIsPaused((prev) => !prev)}
            className={heroVideoPlaybackButtonClassName}
          >
            {isPaused ? (
              <Play aria-hidden className="size-4 shrink-0" />
            ) : (
              <Pause aria-hidden className="size-4 shrink-0" />
            )}
            {isPaused ? playLabel : pauseLabel}
          </button>
        </div>
      )}
    </div>
  )
}
